import type { FeatureGroup, FeatureItem } from '@aibees/shared/definition';
import type { CatalogFeature, FeatureCatalog } from './types';
import {
  composeTitle,
  expandSupportLadder,
  representativeCell,
  type GroupAcc,
  type RowWithOrder,
} from './utils';

const SUPPORT_SECTION = 'Support & Success';

function buildFeatureItem(
  feature: CatalogFeature,
  productId: string,
  slug: string
): FeatureItem | null {
  const isInProduct = feature.products.includes(productId);
  const cell = isInProduct
    ? feature.cells[productId]
    : representativeCell(feature);

  if (!cell?.section) return null;

  const item: FeatureItem = {
    id: cell.code || feature.key,
    title: composeTitle(feature.name, cell, slug),
    description: cell.tooltip,
    disabled: !isInProduct,
    comingSoon: cell.coming_soon === 'true',
  };

  if (cell.badge) {
    item.poweredBy = cell.badge.replace(/^Powered by /, '');
  }

  return item;
}

export function buildFeatureGroups(
  productId: string,
  catalog: FeatureCatalog
): FeatureGroup[] {
  const product = catalog.products.find((p) => p.id === productId);
  const slug = product?.metadata.slug ?? '';
  const groupMap = new Map<string, GroupAcc>();

  for (const feature of catalog.features) {
    const cell = feature.cells[productId] ?? representativeCell(feature);
    if (!cell?.section) continue;

    const item = buildFeatureItem(feature, productId, slug);
    if (!item) continue;

    const order = Number(cell.section_order ?? 999);
    const itemWithOrder: RowWithOrder = { ...item, _order: order };

    const existing = groupMap.get(cell.section);
    if (existing) {
      existing.items.push(itemWithOrder);
      existing.minOrder = Math.min(existing.minOrder, order);
    } else {
      groupMap.set(cell.section, {
        name: cell.section,
        minOrder: order,
        items: [itemWithOrder],
      });
    }
  }

  const supportGroup = groupMap.get(SUPPORT_SECTION);
  if (supportGroup) expandSupportLadder(supportGroup);

  const groups = Array.from(groupMap.values()).sort(
    (a, b) => a.minOrder - b.minOrder
  );

  return groups.map((g) => ({
    title: g.name,
    features: g.items
      .sort((a, b) => a._order - b._order)
      .map(({ _order: _unused, ...item }) => item),
  }));
}
